import { PrefetchKind } from 'next/dist/client/components/router-reducer/router-reducer-types';

type RoutePrefetcher = {
  prefetch: (href: string, options: { kind: PrefetchKind }) => void;
};

// Next keeps a full prefetch for five minutes; re-request a little sooner.
const THREAD_ROUTE_PREFETCH_TTL_MS = 4 * 60_000;
const prefetchedAt = new Map<string, number>();

export function threadRoutePath(orgSlug: string, routeRef: string): string {
  return `/${orgSlug}/threads/${routeRef}`;
}

/**
 * Full RSC prefetch of a thread page. The default (auto) prefetch stops at
 * the route's loading boundary, so opening the thread still showed its
 * skeleton while the page loaded; a full prefetch includes the server-
 * rendered page and its snapshot, so the click renders it at once. Live
 * subscriptions take over after mount, as on a direct load.
 */
export function prefetchThreadRoute(
  router: RoutePrefetcher,
  orgSlug: string | undefined,
  routeRef: string | undefined,
  now = Date.now()
): boolean {
  if (!orgSlug || !routeRef) return false;
  return prefetchFullRoute(router, threadRoutePath(orgSlug, routeRef), now);
}

/**
 * Full RSC prefetch of any app route, at most once per TTL. Used where a
 * click should not wait on the route's server round trip (thread pages, My
 * Drive folders and files). Next skips prefetching in development.
 */
export function prefetchFullRoute(
  router: RoutePrefetcher,
  path: string,
  now = Date.now()
): boolean {
  if (!path) return false;
  const previous = prefetchedAt.get(path);
  if (previous !== undefined && now - previous < THREAD_ROUTE_PREFETCH_TTL_MS)
    return false;
  prefetchedAt.set(path, now);
  router.prefetch(path, { kind: PrefetchKind.FULL });
  return true;
}

export function resetThreadRoutePrefetchesForTests(): void {
  prefetchedAt.clear();
}
